<template>
    <div class="q-gutter-y-md">
        <div class="row q-col-gutter-md items-center">
            <div class="col-12 col-md-4">
                <q-input
                    v-model="filter.search"
                    label="Search IP Address or Reason"
                    dense
                    outlined
                    debounce="300"
                    clearable
                >
                    <template v-slot:append>
                        <q-icon name="search" />
                    </template>
                </q-input>
            </div>
            <div class="col-12 col-md-3">
                <q-select
                    v-model="filter.type"
                    :options="typeOptions"
                    label="Filter by Type"
                    dense
                    outlined
                    emit-value
                    map-options
                    clearable
                />
            </div>
            <div class="col-12 col-md-5 text-right">
                <q-btn
                    color="primary"
                    icon="refresh"
                    label="Refresh"
                    flat
                    dense
                    @click="refreshTable"
                />
            </div>
        </div>

        <div
            v-if="selected.length > 0"
            class="q-mb-md q-pa-md bg-grey-2 rounded-borders flex justify-between items-center animate-fade"
        >
            <div class="text-subtitle2 text-grey-8">
                {{ selectedLabel }}
            </div>
            <div class="q-gutter-sm flex items-center">
                <q-btn-dropdown
                    color="primary"
                    icon="settings"
                    label="Bulk Actions"
                    outlined
                    dense
                    id="bulk-actions-dropdown"
                >
                    <q-list>
                        <q-item
                            clickable
                            v-close-popup
                            @click="confirmBulkWhitelist"
                        >
                            <q-item-section avatar>
                                <q-icon name="check_circle" color="positive" />
                            </q-item-section>
                            <q-item-section>
                                Whitelist Selected
                            </q-item-section>
                        </q-item>

                        <q-item
                            clickable
                            v-close-popup
                            @click="confirmBulkBlacklist"
                        >
                            <q-item-section avatar>
                                <q-icon name="block" color="negative" />
                            </q-item-section>
                            <q-item-section>
                                Blacklist Selected
                            </q-item-section>
                        </q-item>

                        <q-separator />

                        <q-item
                            clickable
                            v-close-popup
                            class="text-negative"
                            @click="confirmBulkUnblock"
                        >
                            <q-item-section avatar>
                                <q-icon name="restart_alt" color="secondary" />
                            </q-item-section>
                            <q-item-section>
                                Reset / Unblock Selected
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-btn-dropdown>
                <q-btn
                    flat
                    dense
                    color="grey-7"
                    label="Clear Selection"
                    @click="clearSelection"
                />
            </div>
        </div>

        <q-table
            class="sticky-container-table"
            title="Known IP Addresses"
            :rows="rows"
            :columns="columns"
            v-model:pagination="pagination"
            :loading="loading"
            @request="onRequest"
            binary-state-sort
            row-key="id"
            selection="multiple"
            v-model:selected="selected"
            flat
            bordered
        >
            <template v-slot:body-cell-ip_address="props">
                <q-td :props="props">
                    <a
                        :href="'/dev/security/ips/' + props.row.ip_address"
                        class="text-primary text-weight-bold"
                    >
                        {{ props.row.ip_address }}
                    </a>
                </q-td>
            </template>

            <template v-slot:body-cell-type="props">
                <q-td :props="props">
                    <q-badge
                        :color="getTypeBadgeColor(props.row.type)"
                        :label="capitalize(props.row.type)"
                    />
                </q-td>
            </template>

            <template v-slot:body-cell-status="props">
                <q-td :props="props">
                    <q-badge
                        v-if="props.row.card === 'red'"
                        color="negative"
                        label="Red Card (Blocked)"
                    />
                    <q-badge
                        v-else-if="props.row.card === 'yellow'"
                        color="warning"
                        text-color="dark"
                        label="Yellow Card"
                    />
                    <q-badge
                        v-else-if="props.row.type === 'blacklist'"
                        color="negative"
                        label="Blacklisted"
                    />
                    <q-badge
                        v-else-if="props.row.type === 'whitelist'"
                        color="positive"
                        label="Whitelisted"
                    />
                    <q-badge
                        v-else
                        color="blue-grey"
                        label="Clean"
                    />
                    <span v-if="props.row.strikes > 0" class="text-caption text-grey q-ml-xs">
                        ({{ props.row.strikes }} strike{{ props.row.strikes > 1 ? 's' : '' }})
                    </span>
                </q-td>
            </template>

            <template v-slot:body-cell-abuseScore="props">
                <q-td :props="props" class="text-center">
                    <q-badge
                        v-if="props.row.abuseScore !== null && props.row.abuseScore !== undefined"
                        :color="getAbuseScoreColor(props.row.abuseScore)"
                        :label="props.row.abuseScore + '%'"
                    />
                    <span v-else class="text-grey text-caption">-</span>
                </q-td>
            </template>

            <template v-slot:body-cell-last_access_at="props">
                <q-td :props="props">
                    <div v-if="props.row.last_access_at">
                        <div>{{ props.row.last_access_at }}</div>
                        <div class="text-grey text-caption">
                            {{ props.row.last_access_at_human }}
                        </div>
                    </div>
                    <span v-else class="text-grey">Never</span>
                </q-td>
            </template>

            <template v-slot:body-cell-actions="props">
                <q-td :props="props" class="text-center">
                    <div class="row q-gutter-xs justify-center no-wrap">
                        <q-btn
                            size="sm"
                            color="primary"
                            icon="visibility"
                            :href="'/dev/security/ips/' + props.row.ip_address"
                            title="Details"
                        />
                        <q-btn
                            v-if="props.row.type !== 'whitelist'"
                            size="sm"
                            color="positive"
                            icon="check_circle"
                            title="Whitelist IP"
                            @click="whitelistIp(props.row.ip_address)"
                        />
                        <q-btn
                            v-if="props.row.type !== 'blacklist'"
                            size="sm"
                            color="negative"
                            icon="block"
                            title="Blacklist IP"
                            @click="blacklistIp(props.row.ip_address)"
                        />
                        <q-btn
                            size="sm"
                            color="secondary"
                            icon="restart_alt"
                            title="Reset / Unblock IP"
                            @click="unblockIp(props.row.ip_address)"
                        />
                    </div>
                </q-td>
            </template>
        </q-table>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import axios from "axios";
import { useQuasar } from "quasar";

export default {
    name: "DevSecurityIpsTable",
    setup() {
        const $q = useQuasar();
        const rows = ref([]);
        const loading = ref(false);
        const selected = ref([]);
        const pagination = ref({
            sortBy: "last_access_at",
            descending: true,
            page: 1,
            rowsPerPage: 15,
            rowsNumber: 0,
        });

        const filter = ref({
            search: "",
            type: null,
        });

        const selectedLabel = computed(() => {
            const count = selected.value.length;
            if (count === 1) {
                return "1 IP address selected";
            }
            return `${count} IP addresses selected`;
        });

        const clearSelection = () => {
            selected.value = [];
        };

        const typeOptions = [
            { label: "All Types", value: null },
            { label: "Neutral", value: "neutral" },
            { label: "Whitelist", value: "whitelist" },
            { label: "Blacklist", value: "blacklist" },
        ];

        const columns = [
            {
                name: "ip_address",
                label: "IP Address",
                field: "ip_address",
                sortable: true,
                align: "left",
            },
            {
                name: "type",
                label: "Type",
                field: "type",
                sortable: true,
                align: "left",
            },
            {
                name: "status",
                label: "Status / Card",
                field: "status",
                sortable: false,
                align: "left",
            },
            {
                name: "abuseScore",
                label: "Abuse Score",
                field: "abuseScore",
                sortable: false,
                align: "center",
            },
            {
                name: "reason",
                label: "Reason",
                field: "reason",
                sortable: false,
                align: "left",
            },
            {
                name: "last_access_at",
                label: "Last Access",
                field: "last_access_at",
                sortable: true,
                align: "left",
            },
            {
                name: "created_at",
                label: "First Recorded",
                field: "created_at",
                sortable: true,
                align: "left",
            },
            {
                name: "actions",
                label: "Actions",
                field: "actions",
                align: "center",
            },
        ];

        const capitalize = (str) => {
            if (!str) return "";
            return str.charAt(0).toUpperCase() + str.slice(1);
        };

        const getTypeBadgeColor = (type) => {
            switch (type) {
                case "whitelist":
                    return "positive";
                case "blacklist":
                    return "negative";
                case "neutral":
                default:
                    return "blue-grey-6";
            }
        };

        const getAbuseScoreColor = (score) => {
            if (score >= 90) return "negative";
            if (score >= 50) return "warning";
            if (score > 0) return "amber-8";
            return "positive";
        };

        const fetchFromServer = (
            start,
            limit,
            sortBy,
            descending,
            filterValues
        ) => {
            const params = {
                start,
                limit,
                sortBy,
                descending,
                search: filterValues.search || undefined,
                type: filterValues.type || undefined,
            };
            return axios.get("/dev/api/security/ips", { params });
        };

        const onRequest = (props) => {
            const { page, rowsPerPage, sortBy, descending } = props.pagination;
            const filterValues = filter.value;

            loading.value = true;

            const start = (page - 1) * rowsPerPage;
            const limit = rowsPerPage;

            fetchFromServer(start, limit, sortBy, descending, filterValues)
                .then((response) => {
                    rows.value = response.data.rows;
                    pagination.value.rowsNumber = response.data.rowsNumber;

                    pagination.value.page = page;
                    pagination.value.rowsPerPage = rowsPerPage;
                    pagination.value.sortBy = sortBy;
                    pagination.value.descending = descending;

                    loading.value = false;
                })
                .catch((error) => {
                    loading.value = false;
                    $q.notify({
                        color: "negative",
                        message: "Failed to fetch IP addresses",
                        icon: "report_problem",
                    });
                });
        };

        const refreshTable = () => {
            onRequest({ pagination: pagination.value });
        };

        const whitelistIp = (ip) => {
            $q.dialog({
                title: "Whitelist IP",
                message: `Are you sure you want to whitelist ${ip}?`,
                cancel: true,
                persistent: true,
            }).onOk(() => {
                axios
                    .post(`/dev/security/ips/${encodeURIComponent(ip)}/whitelist`)
                    .then((response) => {
                        $q.notify({
                            color: "positive",
                            message: response.data.message || `IP ${ip} whitelisted successfully`,
                            icon: "check",
                        });
                        refreshTable();
                    })
                    .catch(() => {
                        $q.notify({
                            color: "negative",
                            message: "Failed to whitelist IP",
                            icon: "report_problem",
                        });
                    });
            });
        };

        const blacklistIp = (ip) => {
            $q.dialog({
                title: "Blacklist IP",
                message: `Enter reason for blacklisting ${ip}:`,
                prompt: {
                    model: "Blacklisted via Dev Area",
                    type: "text",
                },
                cancel: true,
                persistent: true,
            }).onOk((reason) => {
                axios
                    .post(`/dev/security/ips/${encodeURIComponent(ip)}/blacklist`, { reason })
                    .then((response) => {
                        $q.notify({
                            color: "positive",
                            message: response.data.message || `IP ${ip} blacklisted successfully`,
                            icon: "check",
                        });
                        refreshTable();
                    })
                    .catch(() => {
                        $q.notify({
                            color: "negative",
                            message: "Failed to blacklist IP",
                            icon: "report_problem",
                        });
                    });
            });
        };

        const unblockIp = (ip) => {
            $q.dialog({
                title: "Reset & Clear IP",
                message: `Are you sure you want to unblock and clear all records and flags for ${ip}?`,
                cancel: true,
                persistent: true,
            }).onOk(() => {
                axios
                    .post(`/dev/security/ips/${encodeURIComponent(ip)}/unblock`)
                    .then((response) => {
                        $q.notify({
                            color: "positive",
                            message: response.data.message || `IP ${ip} reset successfully`,
                            icon: "check",
                        });
                        refreshTable();
                    })
                    .catch(() => {
                        $q.notify({
                            color: "negative",
                            message: "Failed to unblock IP",
                            icon: "report_problem",
                        });
                    });
            });
        };

        const executeBulkAction = (action, reason = null) => {
            if (selected.value.length === 0) return;

            loading.value = true;
            const ips = selected.value.map((row) => row.ip_address);

            axios
                .post("/dev/security/ips/bulk", {
                    action,
                    ips,
                    reason,
                })
                .then((response) => {
                    $q.notify({
                        color: "positive",
                        message:
                            response.data.message ||
                            "Bulk action executed successfully.",
                        icon: "check",
                    });
                    clearSelection();
                    refreshTable();
                })
                .catch((error) => {
                    loading.value = false;
                    $q.notify({
                        color: "negative",
                        message:
                            error.response?.data?.message ||
                            "Failed to execute bulk action.",
                        icon: "report_problem",
                    });
                });
        };

        const confirmBulkWhitelist = () => {
            const count = selected.value.length;
            $q.dialog({
                title: "Whitelist Selected IPs",
                message: `Are you sure you want to whitelist ${count} selected IP address${count > 1 ? "es" : ""}?`,
                cancel: true,
                persistent: true,
            }).onOk(() => {
                executeBulkAction("whitelist");
            });
        };

        const confirmBulkBlacklist = () => {
            const count = selected.value.length;
            $q.dialog({
                title: "Blacklist Selected IPs",
                message: `Enter reason for blacklisting ${count} selected IP address${count > 1 ? "es" : ""}:`,
                prompt: {
                    model: "Blacklisted via Dev Area Bulk Action",
                    type: "text",
                },
                cancel: true,
                persistent: true,
            }).onOk((reason) => {
                executeBulkAction("blacklist", reason);
            });
        };

        const confirmBulkUnblock = () => {
            const count = selected.value.length;
            $q.dialog({
                title: "Reset & Clear Selected IPs",
                message: `Are you sure you want to unblock and clear all records and flags for ${count} selected IP address${count > 1 ? "es" : ""}?`,
                cancel: true,
                persistent: true,
            }).onOk(() => {
                executeBulkAction("unblock");
            });
        };

        // Watch filters to trigger reload
        watch(
            filter,
            () => {
                clearSelection();
                pagination.value.page = 1;
                onRequest({ pagination: pagination.value });
            },
            { deep: true }
        );

        onMounted(() => {
            onRequest({ pagination: pagination.value });
        });

        return {
            rows,
            columns,
            loading,
            selected,
            selectedLabel,
            clearSelection,
            pagination,
            filter,
            typeOptions,
            capitalize,
            getTypeBadgeColor,
            getAbuseScoreColor,
            onRequest,
            refreshTable,
            whitelistIp,
            blacklistIp,
            unblockIp,
            executeBulkAction,
            confirmBulkWhitelist,
            confirmBulkBlacklist,
            confirmBulkUnblock,
        };
    },
};
</script>
